import { useEffect, useId, useRef, useState } from 'react';

import { Avatar } from '@/components/ui';
import { NavIcon } from '@/components/icons/NavIcon';
import { useEmailSuggestions } from '@/hooks';
import type { UserSummary } from '@/api/types';
import { emailLocal } from '@/lib/format';

import styles from '../MemberPerms.module.css';

interface MemberSearchBoxProps {
  searchQ: string;
  onChangeSearchQ: (value: string) => void;
  onPickMember: (user: UserSummary) => void;
  spotlight?: boolean;
  autoFocus?: boolean;
  // 검색 결과에 없는 이메일도 직접 입력해 선택 가능. backend가 도메인 정책으로 거부하면 grant 시도 시 에러 토스트.
  onPickManualEmail: (email: string) => void;
}

// 멤버 검색 / 자동완성 박스.
export default function MemberSearchBox({
  searchQ,
  onChangeSearchQ,
  onPickMember,
  onPickManualEmail,
  spotlight = false,
  autoFocus = false,
}: MemberSearchBoxProps) {
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  const suggestion = useEmailSuggestions(searchQ);

  const trimmedQ = searchQ.trim().toLowerCase();
  const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedQ);
  const isExactMatch = suggestion.results.some((u) => u.email.toLowerCase() === trimmedQ);
  const canAddManually = looksLikeEmail && !isExactMatch;
  const showDropdown = suggestOpen && (suggestion.results.length > 0 || canAddManually);
  const optionCount = suggestion.results.length + Number(canAddManually);

  useEffect(() => { setHighlightedIndex(-1); }, [searchQ]);
  useEffect(() => {
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (searchRef.current?.contains(event.target as Node)) return;
      setSuggestOpen(false);
      setHighlightedIndex(-1);
    };
    document.addEventListener('pointerdown', closeOnOutsideClick);
    return () => document.removeEventListener('pointerdown', closeOnOutsideClick);
  }, []);

  const selectOption = (index: number) => {
    const member = suggestion.results[index];
    setSuggestOpen(false);
    setHighlightedIndex(-1);
    if (member) onPickMember(member);
    else if (canAddManually && index === suggestion.results.length) onPickManualEmail(trimmedQ);
  };

  return (
    <div className={`${styles.lookupRow} ${spotlight ? styles.lookupRowSpotlight : ''}`}>
      <div className={styles.searchWrap} ref={searchRef}>
        <div className={`${styles.lookupInput} ${spotlight ? styles.lookupInputSpotlight : ''}`}>
          <NavIcon name="search" size={spotlight ? 19 : 14} color="#9B9B9B" />
          <input
            className={styles.lookupInputEl}
            autoFocus={autoFocus}
            value={searchQ}
            onChange={(e) => {
              onChangeSearchQ(e.target.value);
              setSuggestOpen(true);
            }}
            onFocus={() => setSuggestOpen(true)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                setSuggestOpen(true);
                if (!optionCount) return;
                setHighlightedIndex(previous => {
                  if (previous < 0) return event.key === 'ArrowDown' ? 0 : optionCount - 1;
                  return event.key === 'ArrowDown'
                    ? (previous + 1) % optionCount
                    : (previous - 1 + optionCount) % optionCount;
                });
              } else if (event.key === 'Enter' && showDropdown && highlightedIndex >= 0) {
                event.preventDefault();
                selectOption(highlightedIndex);
              } else if (event.key === 'Escape') {
                event.preventDefault();
                setSuggestOpen(false);
                setHighlightedIndex(-1);
              }
            }}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={showDropdown}
            aria-controls={showDropdown ? listboxId : undefined}
            aria-activedescendant={highlightedIndex >= 0 ? `${listboxId}-option-${highlightedIndex}` : undefined}
            placeholder="이메일로 멤버 검색"
          />
        </div>
        {showDropdown && (
          <div className={styles.suggest} id={listboxId} role="listbox">
            {suggestion.results.map((u, index) => (
              <button
                key={u.email}
                id={`${listboxId}-option-${index}`}
                type="button"
                role="option"
                aria-selected={highlightedIndex === index}
                className={`${styles.suggestItem} ${highlightedIndex === index ? styles.suggestItemActive : ''}`}
                onMouseEnter={() => setHighlightedIndex(index)}
                onClick={() => selectOption(index)}
              >
                <Avatar email={u.email} name={emailLocal(u.email)} size={28} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className={styles.suggestEmail}>{u.email}</div>
                  <div className={styles.suggestMeta}>
                    {u.platform_role} · grants {u.grants_count}
                    {u.has_all_scope && ' · all'}
                  </div>
                </div>
              </button>
            ))}
            {canAddManually && (
              <button
                type="button"
                id={`${listboxId}-option-${suggestion.results.length}`}
                role="option"
                aria-selected={highlightedIndex === suggestion.results.length}
                className={`${styles.suggestItem} ${highlightedIndex === suggestion.results.length ? styles.suggestItemActive : ''}`}
                onMouseEnter={() => setHighlightedIndex(suggestion.results.length)}
                onClick={() => selectOption(suggestion.results.length)}
                style={{
                  borderTop: suggestion.results.length > 0 ? '1px solid #F4F4F4' : undefined,
                }}
              >
                <Avatar email={trimmedQ} name={emailLocal(trimmedQ)} size={28} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className={styles.suggestEmail}>+ 이 이메일로 추가</div>
                  <div className={styles.suggestMeta}>{trimmedQ}</div>
                </div>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
